import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = ["Votre compte", "Votre association"];

export const AdminRegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    email: "",
    association_name: "",
    association_slug: "",
    contact_email: "",
    city: "",
  });

  const set = (key) => (e) => {
    const value = key === "association_slug"
      ? e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
      : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/admin/request-access`, {
        email: form.email,
        association_name: form.association_name,
        association_slug: form.association_slug,
        contact_email: form.contact_email || undefined,
        city: form.city || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Une erreur est survenue.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Shell>
        <div className="text-center space-y-5">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-slate-900 font-medium">Demande envoyée</p>
            <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">
              Votre demande a été soumise. Vous recevrez un email d'activation à{" "}
              <span className="font-medium text-slate-700">{form.email}</span> une fois votre compte validé.
            </p>
          </div>
          <Link to="/admin/login" className="inline-block text-emerald-600 text-sm hover:text-emerald-700 transition-colors">
            ← Retour à la connexion
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title="Créer votre espace"
      subtitle="Enregistrez votre association sur la plateforme Hafani."
    >
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all ${
              i < step ? "bg-emerald-600 text-white" :
              i === step ? "bg-slate-900 text-white" :
              "bg-slate-100 text-slate-400"
            }`}>
              {i < step ? (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className={`text-xs ${i === step ? "text-slate-700 font-medium" : "text-slate-400"}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <div className={`h-px flex-1 ml-1 ${i < step ? "bg-emerald-300" : "bg-slate-100"}`} />
            )}
          </div>
        ))}
      </div>

      {step === 0 && (
        <form onSubmit={handleNext} className="space-y-4">
          <FormField
            label="Votre adresse email"
            type="email"
            value={form.email}
            onChange={set("email")}
            required
            placeholder="vous@association.fr"
          />
          <p className="text-xs text-slate-400 -mt-1">
            C'est l'adresse sur laquelle vous recevrez votre lien d'activation.
          </p>
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.98] transition-all mt-2"
          >
            Continuer →
          </button>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label="Nom de l'association"
            value={form.association_name}
            onChange={set("association_name")}
            required
            placeholder="ex. AMPA France"
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
              Identifiant unique <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.association_slug}
              onChange={set("association_slug")}
              required
              placeholder="ex. ampa-france"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
            />
            <p className="text-xs text-slate-400">Utilisé dans l'URL de votre espace. Lettres minuscules et tirets uniquement.</p>
          </div>
          <FormField
            label="Email de contact (optionnel)"
            type="email"
            value={form.contact_email}
            onChange={set("contact_email")}
            placeholder="contact@association.fr"
          />
          <FormField
            label="Ville (optionnel)"
            value={form.city}
            onChange={set("city")}
            placeholder="Paris"
          />
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => setStep(0)}
              className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 active:scale-[0.98] transition-all"
            >
              ← Retour
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {loading ? "Envoi…" : "Soumettre la demande"}
            </button>
          </div>
        </form>
      )}

      <div className="text-center mt-4">
        <Link to="/admin/login" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
          Déjà un compte ? Se connecter
        </Link>
      </div>
    </Shell>
  );
};

const Shell = ({ children, title, subtitle }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center px-4"
    style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}
  >
    <div className="w-full max-w-md">
      <div className="text-center mb-10">
        <p className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-2">Hafani MS</p>
        <h1
          className="text-4xl text-slate-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
        >
          {title || "Inscription"}
        </h1>
        {subtitle && <p className="text-slate-500 text-sm mt-3 leading-relaxed">{subtitle}</p>}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 px-8 py-8">
        {children}
      </div>
    </div>
  </div>
);

const FormField = ({ label, type = "text", value, onChange, required, placeholder }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
    />
  </div>
);
